import _ from 'lodash';
import React from 'react';

import moment from 'moment';
import {connect} from 'react-redux';
import {reduxForm, formValueSelector, Field} from 'redux-form';

import {required, validDate} from 'utils/validators';
import {
  formatDateForApi,
  formatDate,
  getNextMonthFirstDay,
  DATE_FORMAT
} from 'utils/datetime';

import ErrorBlock from 'components/ErrorBlock';
import Button from 'components/Button';
import FormField from 'components/FormField';
import {DateField, SelectField, NumberField} from 'components/Fields';
import Label from 'components/Label';
import Warning from 'components/Warning';

import {
  MODALS,
  selectBooking,
  applyRentSchedule,
  generateRentSchedule
} from 'ducks/bookings';
import {composeActions} from 'ducks/common';
import {storeDataInModal} from 'ducks/modals';

import './styles.css';

const DEFAULT_INVOICES_TO_CHARGE_BEFORE_MOVE_IN = 2;
const WEEKLY = 'weekly';
const MONTHLY = 'monthly';
const QUARTERLY = 'quarterly';
const UPFRONT = 'upfront';
const NEW_MONTHLY = 'new_monthly';

const BILLING_FREQUENCY_OPTIONS = [
  {
    label: 'Monthly',
    value: NEW_MONTHLY
  },
  {
    label: 'Upfront',
    value: UPFRONT
  },
  {
    label: 'Old Monthly',
    value: MONTHLY
  },
  {
    label: 'Old Quarterly',
    value: QUARTERLY
  },
  {
    label: 'Old Weekly',
    value: WEEKLY
  }
];

const START_DATE_IN_THE_PAST_WARNING = "Booking's start date is in the past";

const constructCreateRentScheduleData = ({
  bookingId,
  billingDate,
  billingFrequency,
  invoicesToChargeBeforeMoveIn,
  preArrivalInvoicesDue = null,
  rentPerMonth = 0
}) => ({
  booking: bookingId,
  billingDate: formatDateForApi(billingDate),
  billingFrequency: _.get(billingFrequency, 'value'),
  invoicesToChargeBeforeMoveIn,
  preArrivalInvoicesDue: formatDateForApi(preArrivalInvoicesDue),
  rentPerMonth
});

const formSubmit = (values, dispatch, props) => {
  const {bookingId, applyRentScheduleExtraActions} = props;
  const {
    billingDate,
    billingFrequency,
    invoicesToChargeBeforeMoveIn,
    preArrivalInvoicesDue,
    rentPerMonth
  } = values;

  const data = constructCreateRentScheduleData({
    bookingId,
    billingDate,
    billingFrequency,
    invoicesToChargeBeforeMoveIn,
    preArrivalInvoicesDue,
    rentPerMonth
  });

  dispatch(applyRentSchedule(data, applyRentScheduleExtraActions()));
};

class CreateRentScheduleForm extends React.Component<any> {
  state = {
    showDueDateInput: false,
    showBillingDateInput: true,
    showRentPerMonth: false,
    showPreArrivalInvoicesCount: true
  };

  generateRentSchedule = () => {
    const {
      bookingId,
      billingDate,
      billingFrequency,
      invoicesToChargeBeforeMoveIn,
      generateRentScheduleExtraActions,
      preArrivalInvoicesDue,
      rentPerMonth
    } = this.props;

    const data = constructCreateRentScheduleData({
      bookingId,
      billingDate,
      billingFrequency,
      invoicesToChargeBeforeMoveIn,
      preArrivalInvoicesDue,
      rentPerMonth
    });

    this.props.composeActions([
      () =>
        storeDataInModal({
          modalName: MODALS.CREATE_RENT_SCHEDULE,
          data: {generating: true}
        }),
      () => generateRentSchedule(data, generateRentScheduleExtraActions()),
      () =>
        storeDataInModal({
          modalName: MODALS.CREATE_RENT_SCHEDULE,
          data: {generating: false}
        })
    ]);
  };

  onBillingFrequencyChange = data => {
    const isUpfront = data.value === UPFRONT;
    const isWeekly = data.value === WEEKLY;
    const isMonthly = data.value === MONTHLY;
    const isQuarterly = data.value === QUARTERLY;
    const isNewMonthly = data.value === NEW_MONTHLY;

    if (isWeekly) {
      const {startDate} = this.props;

      this.props.change('billingDate', formatDate(startDate));
    }

    if (isMonthly || isQuarterly) {
      this.props.change(
        'invoicesToChargeBeforeMoveIn',
        DEFAULT_INVOICES_TO_CHARGE_BEFORE_MOVE_IN
      );
    }

    if (isWeekly) {
      this.setState({
        showDueDateInput: true,
        showBillingDateInput: true,
        showRentPerMonth: false,
        showPreArrivalInvoicesCount: true
      });
    }

    if (isMonthly) {
      this.setState({
        showDueDateInput: true,
        showBillingDateInput: true,
        showRentPerMonth: false,
        showPreArrivalInvoicesCount: true
      });
    }

    if (isQuarterly) {
      this.setState({
        showDueDateInput: true,
        showBillingDateInput: true,
        showRentPerMonth: false,
        showPreArrivalInvoicesCount: true
      });
    }

    if (isNewMonthly) {
      this.props.change('invoicesToChargeBeforeMoveIn', 1);
      this.setState({
        showDueDateInput: true,
        showBillingDateInput: false,
        showRentPerMonth: true,
        showPreArrivalInvoicesCount: true
      });
    }

    if (isUpfront) {
      this.setState({
        showDueDateInput: true,
        showBillingDateInput: false,
        showRentPerMonth: false,
        showPreArrivalInvoicesCount: false
      });
    }
  };

  render() {
    const {error, generating, invalid, startDate} = this.props;
    const {
      showBillingDateInput,
      showDueDateInput,
      showRentPerMonth,
      showPreArrivalInvoicesCount
    } = this.state;

    const startDateIsInThePast =
      startDate && moment(startDate, DATE_FORMAT).isBefore(moment(), 'day');
    const generateDisabled = generating || invalid;

    return (
      <div className="CreateRentScheduleForm">
        {startDateIsInThePast && (
          <Warning>{START_DATE_IN_THE_PAST_WARNING}</Warning>
        )}
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <form>
          {showBillingDateInput && (
            <FormField className="billingDateFormField">
              <Label>Billing date</Label>
              <Field
                name="billingDate"
                validate={[validDate]}
                component={DateField}
              />
            </FormField>
          )}
          <FormField className="billingFrequencyFormField">
            <Label>Bill frequency</Label>
            <Field
              name="billingFrequency"
              placeholder="Select billing frequency"
              validate={[required]}
              component={SelectField}
              options={BILLING_FREQUENCY_OPTIONS}
              onChange={this.onBillingFrequencyChange}
            />
          </FormField>
          {showPreArrivalInvoicesCount && (
            <FormField className="invoicesToChargeBeforeMoveIn">
              <Label>Pre arrival invoices count</Label>
              <Field
                name="invoicesToChargeBeforeMoveIn"
                validate={[required]}
                component={NumberField}
              />
            </FormField>
          )}
          {showDueDateInput && (
            <FormField className="preArrivalInvoicesDue">
              <Label>Pre arrival invoices due date</Label>
              <Field
                name="preArrivalInvoicesDue"
                validate={[validDate]}
                component={DateField}
              />
            </FormField>
          )}
          {showRentPerMonth && (
            <FormField className="rentPerMonth">
              <Label>Rent Per Month</Label>
              <Field
                name="rentPerMonth"
                component={NumberField}
                validate={[required]}
              />
            </FormField>
          )}
          <div>
            <Button
              type="button"
              className="generateRentScheduleButton"
              onClick={this.generateRentSchedule}
              disabled={generateDisabled}
              blue={!generateDisabled}
              isLoading={generating}>
              Generate Schedule
            </Button>
          </div>
        </form>
      </div>
    );
  }
}

const formSelector = formValueSelector('createRentSchedule');

const mapStateToProps = state => {
  const booking = selectBooking(state);
  const bookingId = _.get(booking, 'id');
  const {period: {startDate}} = booking;

  const startDateObject = moment(startDate);
  const billingDateInitialValueDateObj = getNextMonthFirstDay(startDateObject);
  const billingDateInitialValue = formatDate(billingDateInitialValueDateObj);
  let preArrivalInvoicesDueInitialValue = moment(startDate).subtract(
    10,
    'days'
  );

  if (preArrivalInvoicesDueInitialValue < moment()) {
    preArrivalInvoicesDueInitialValue = moment();
  }

  return {
    bookingId,
    startDate: startDateObject,
    invoicesToChargeBeforeMoveIn: formSelector(
      state,
      'invoicesToChargeBeforeMoveIn'
    ),
    billingDate: formSelector(state, 'billingDate'),
    billingFrequency: formSelector(state, 'billingFrequency'),
    preArrivalInvoicesDue: formSelector(state, 'preArrivalInvoicesDue'),
    rentPerMonth: formSelector(state, 'rentPerMonth'),
    initialValues: {
      rentPerMonth: (_.get(booking, 'pwRent', 0) * 52 / 12).toFixed(2),
      invoicesToChargeBeforeMoveIn: DEFAULT_INVOICES_TO_CHARGE_BEFORE_MOVE_IN,
      preArrivalInvoicesDue: formatDate(preArrivalInvoicesDueInitialValue),
      billingDate: billingDateInitialValue
    }
  };
};

const mapDispatchToProps = {
  composeActions
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'createRentSchedule',
    onSubmit: formSubmit,
    onChange: (_values, _dispatch, props) => props.fetchRentScheduleSummary()
  })(CreateRentScheduleForm)
);
